import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Badge } from '../ui/badge.tsx';
import { supabase } from '../../supabase';
import {
  FiTrash2,
  FiEye,
  FiTrendingUp,
  FiBarChart2,
  FiCalendar,
  FiActivity,
  FiX,
  FiLink
} from "react-icons/fi";
import { createShareLink } from '../../lib/api/shareLinks';

const BacktestsViewer = ({ mode = 'backtest' }) => {
  const navigate = useNavigate();
  const [backtests, setBacktests] = useState([]);
  const [error, setError] = useState(null);
  const [selectedBacktest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [sharingId, setSharingId] = useState(null);
  const [shareFeedback, setShareFeedback] = useState(null);

  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';

  // Fetch backtests
  const fetchBacktests = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`${API_BASE}/api/backtests`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch backtests');
      const data = await response.json();
      setBacktests(data.backtests || []);
    } catch (err) {
      setError(err.message);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchBacktests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOptimizationMode = mode === 'optimization';
  const filteredBacktests = useMemo(() => {
    if (!isOptimizationMode) return backtests;
    return backtests.filter((bt) => (
      Boolean(bt?.backtest_results?.optimization || bt?.backtest_results?.is_optimization)
    ));
  }, [backtests, isOptimizationMode]);

  const headerTitle = isOptimizationMode ? 'Optimizer Results' : 'Backtest Results';
  const headerSubtitle = isOptimizationMode
    ? 'View and analyze your optimizer runs'
    : 'View and analyze your backtest results';
  const ctaLabel = isOptimizationMode ? 'Run New Optimization' : 'Run New Backtest';

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this backtest?')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`${API_BASE}/api/backtests/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to delete backtest');

      await fetchBacktests();
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle view details - navigate to ScreenBuilder with backtest ID for server fetch
  const handleViewDetails = (backtest) => {
    console.log('BacktestsViewer: handleViewDetails called with backtest ID:', backtest.id);

    // Navigate to ScreenBuilder with just the backtest ID - let ScreenBuilder fetch from server
    navigate('/dashboard/strategies', {
      state: {
        loadBacktestId: backtest.id
      }
    });
  };

  const handleShare = async (backtestId) => {
    setShareFeedback(null);
    try {
      setSharingId(backtestId);
      const shareId = await createShareLink('backtest', backtestId);
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt('Copy share link:', shareUrl);
      }
      setShareFeedback({ id: backtestId, message: 'Share link copied to clipboard.' });
    } catch (err) {
      setShareFeedback({
        id: backtestId,
        message: err.message || 'Failed to create share link',
        isError: true
      });
    } finally {
      setSharingId(null);
    }
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  // Format number
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(decimals);
  };

  // Get performance color
  const getPerformanceColor = (value) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="backtests-viewer">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-3xl font-bold text-gray-900">{headerTitle}</h1>
          <div className="flex items-center gap-3">
            {isOptimizationMode && (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard/charts')}
                  className="flex items-center gap-2 px-5 py-3 text-lg"
                >
                  <FiActivity />
                  Chart
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard/seasonality')}
                  className="flex items-center gap-2 px-5 py-3 text-lg"
                >
                  <FiCalendar />
                  Seasonality
                </Button>
              </>
            )}
            <Button
              onClick={() => navigate('/dashboard/strategies')}
              className="flex items-center gap-2 px-6 py-3 text-lg"
            >
              <FiTrendingUp />
              {ctaLabel}
            </Button>
          </div>
        </div>
        <p className="text-gray-600">{headerSubtitle}</p>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-r-lg mb-6 shadow-sm">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Backtests List */}
      <div className="space-y-4 mb-8">
        {filteredBacktests.map((backtest) => {
          const metrics = backtest.backtest_results?.metrics || {};
          const tradeMetrics = backtest.backtest_results?.trade_metrics || {};
          const backtestCfg = backtest.backtest_config || {};
          const rebalanceFreq = backtestCfg.rebalance?.freq || backtest.strategy_config?.rebalance?.freq || 'N/A';
          const weightingMethod = backtestCfg.weighting?.method || backtest.strategy_config?.weighting?.method || 'N/A';
          const selection = backtest.strategy_config?.selection || {};
          const rankPick = selection.rankPick || {};
          const selectionFactorRaw = rankPick.formula || rankPick.factor || selection.factor || '';
          const selectionFactor = selectionFactorRaw
            ? selectionFactorRaw.replace(/^formula:/, 'formula ')
            : 'N/A';
          const selectionLabel = (() => {
            const selectionPercent = Number(selection.percent);
            if (Number.isFinite(selectionPercent)) {
              return `${selectionPercent}% Top`;
            }
            const method = rankPick.method || '';
            const k = Number(rankPick.k ?? rankPick.n ?? rankPick.value);
            if (!Number.isFinite(k)) return 'Selection N/A';
            if (method === 'top_pct') return `${k}% Top`;
            if (method === 'bottom_pct') return `${k}% Bottom`;
            if (method === 'top_n') return `${k} Top`;
            if (method === 'bottom_n') return `${k} Bottom`;
            if (method) return `${k} ${method.replace(/_/g, ' ')}`;
            return `${k} Selected`;
          })();

          return (
            <div key={backtest.id} className="border-2 border-gray-300 rounded-lg p-4 hover:shadow-lg transition-all bg-white hover:border-purple-500 shadow-sm">
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-gray-900 mb-1">{backtest.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Created {new Date(backtest.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(backtest)}
                    className="text-xs hover:bg-purple-50 hover:border-purple-400"
                  >
                    <FiEye className="mr-1 h-3 w-3" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare(backtest.id)}
                    className="text-xs"
                    disabled={sharingId === backtest.id}
                  >
                    <FiLink className="mr-1 h-3 w-3" />
                    {sharingId === backtest.id ? 'Sharing...' : 'Share'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(backtest.id)}
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-400"
                  >
                    <FiTrash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="grid grid-cols-4 gap-3 mb-3 pb-3 border-b border-gray-200">
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">CAGR</div>
                  <div className={`text-lg font-bold ${getPerformanceColor(metrics.cagr)}`}>
                    {formatPercent(metrics.cagr)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Max DD</div>
                  <div className={`text-lg font-bold ${getPerformanceColor(metrics.max_drawdown)}`}>
                    {formatPercent(metrics.max_drawdown)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Sharpe</div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatNumber(metrics.sharpe)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 mb-1">Trades</div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatNumber(tradeMetrics.total_trades, 0)}
                  </div>
                </div>
              </div>

              {/* Strategy + Backtest Configuration Tags */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                  {selectionFactor}
                </Badge>
                <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
                  {selectionLabel}
                </Badge>
                <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700">
                  {rebalanceFreq} Rebalance
                </Badge>
                <Badge variant="secondary" className="text-xs bg-orange-50 text-orange-700">
                  {weightingMethod} Weight
                </Badge>
              </div>
              {shareFeedback?.id === backtest.id && (
                <div className={`mt-3 text-xs ${shareFeedback.isError ? 'text-red-600' : 'text-emerald-600'}`}>
                  {shareFeedback.message}
                </div>
              )}
            </div>
          );
        })}

        {filteredBacktests.length === 0 && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center bg-gray-50">
            <div className="text-gray-400 mb-3">
              <FiBarChart2 className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {isOptimizationMode ? 'No optimizer results yet' : 'No backtests yet'}
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              {isOptimizationMode
                ? 'Create a strategy with x in a formula and run your first optimizer.'
                : 'Create a strategy and run your first backtest to see results here.'}
            </p>
            <Button
              onClick={() => navigate('/dashboard/strategies')}
              className="flex items-center gap-2 mx-auto"
            >
              <FiTrendingUp className="h-4 w-4" />
              {isOptimizationMode ? 'Run Your First Optimization' : 'Run Your First Backtest'}
            </Button>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedBacktest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Backtest Details: {selectedBacktest.name}</CardTitle>
                <Button variant="ghost" onClick={() => setShowDetails(false)}>
                  <FiX />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              {selectedBacktest.backtest_results ? (
                <div className="space-y-6">
                  {/* Key Metrics */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatPercent(selectedBacktest.backtest_results.metrics?.cagr)}
                        </div>
                        <div className="text-sm text-gray-600">CAGR</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className={`text-2xl font-bold ${getPerformanceColor(selectedBacktest.backtest_results.metrics?.max_drawdown)}`}>
                          {formatPercent(selectedBacktest.backtest_results.metrics?.max_drawdown)}
                        </div>
                        <div className="text-sm text-gray-600">Max Drawdown</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(selectedBacktest.backtest_results.metrics?.sharpe)}
                        </div>
                        <div className="text-sm text-gray-600">Sharpe Ratio</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(selectedBacktest.backtest_results.metrics?.calmar_ratio)}
                        </div>
                        <div className="text-sm text-gray-600">Calmar Ratio</div>
                      </div>
                    </div>
                  </div>

                  {/* Trade Metrics */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Trade Metrics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(selectedBacktest.backtest_results.trade_metrics?.total_trades)}
                        </div>
                        <div className="text-sm text-gray-600">Total Trades</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatPercent(selectedBacktest.backtest_results.trade_metrics?.win_rate)}
                        </div>
                        <div className="text-sm text-gray-600">Win Rate</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatPercent(selectedBacktest.backtest_results.trade_metrics?.avg_trade_return)}
                        </div>
                        <div className="text-sm text-gray-600">Avg Trade Return</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(selectedBacktest.backtest_results.trade_metrics?.avg_holding_days)}
                        </div>
                        <div className="text-sm text-gray-600">Avg Holding Days</div>
                      </div>
                    </div>
                  </div>

                  {/* Strategy Configuration */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Strategy Configuration</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <pre className="text-sm overflow-x-auto">
                        {JSON.stringify(selectedBacktest.strategy_config, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No backtest results available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BacktestsViewer;
